import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useGridApiContext } from '@mui/x-data-grid-pro';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import PhotoCell from './Photos/PhotoCell';
import { TimeAdjustmentCell } from './CaseReview';
import { usePersistedDataStore } from '../store';
import logger from '../../utils/logger';
// No need for imports or helper function - we'll use valueOptions from headers directly

// we passs in the already filteredheaders into this component
  const SubjectDataGridRow = ({ headers, subject, columnVisibilityModel, updateRefWithSubj, unsavedChangesRef, handleSetPropertyInfo }) => {
    const apiRef = useGridApiContext();
    const containerRef = useRef(null);
    const [reviewed, setReviewed] = useState(false);
    const [editingCell, setEditingCell] = useState(null);
    const [editableValue, setEditableValue] = useState('');
    const [columnStamp, setColumnStamp] = useState(0);
    
    // Get time adjustment settings from store
    const getNegotiationObj = usePersistedDataStore((state) => state.negotiationObj);
    const caseObject = getNegotiationObj?.cases?.find(c => c.parcel_id === subject.parcel_id);
    const getTimeAdjustmentSettings = () => {
      const timeAdjValue = subject?.TimeAdjSalePrice || subject?.time_adj || 0;
      return {
        ...getNegotiationObj?.timeAdjustmentSettings,
        timeAdjustmentValue: timeAdjValue
      };
    };
    
    useEffect(() => {
      const api = apiRef.current;
      if (!api?.subscribeEvent) return undefined;
      const bump = () => setColumnStamp((n) => n + 1);
      const events = ['columnWidthChange', 'columnResize', 'columnsChange', 'columnVisibilityModelChange', 'columnIndexChange', 'viewportInnerSizeChange'];
      const unsubs = events.map((event) => api.subscribeEvent(event, bump));
      return () => unsubs.forEach((unsub) => unsub?.());
    }, [apiRef]);

    // Match the grid's visible columns, in its order, at the widths it is actually drawing.
    const calculatedHeaders = useMemo(() => {
      const visible = apiRef.current?.getVisibleColumns?.() ?? [];
      const byField = new Map((headers || []).map((header) => [header.field, header]));
      return visible
        .filter((col) => !String(col.field).startsWith('__'))
        .map((col) => ({
          ...(byField.get(col.field) || {}),
          ...col,
          calcWidth: Math.round(col.computedWidth || col.width || 0),
        }));
    }, [apiRef, headers, columnStamp, columnVisibilityModel]);

    const imputedValueCheck = useCallback((header, stringValue) => {
    
        // Ensure you convert dates to values before displaying to prevent errors
        if (header.type==='date') {
            if (stringValue) {
                // Convert stringValue to a Date object if it isn't already
                const dateValue = typeof stringValue === 'string' ? new Date(stringValue) : stringValue;
    
                // Check if the resulting date is valid
                if (!isNaN(dateValue.getTime())) {
                    const month = dateValue.getMonth() + 1;
                    const day = dateValue.getDate();
                    const year = dateValue.getFullYear();
                    const formattedDate = `${month.toString().padStart(2, '0')}/${day.toString().padStart(2, '0')}/${year}`;
                    return <span>{formattedDate}</span>;
                }
                return; // Return nothing if the date is invalid
            }
        }
    
        // Handle other fields
        let value = stringValue;
        if (typeof value === 'string' && value.includes('*')) {
            return (
                <div style={{ color: '#d32f2f' }} className="h-full w-full flex items-center justify-center">
                    <span className="text-red-500 h-full w-full hover:text-blue-500">{value}</span>
                </div>
            );
        } else if (typeof value === 'number') {
            return <span style={{ textAlign: header.align }}>{value.toLocaleString()}</span>;
        }
        return <span style={{ textAlign: header.align }}>{value}</span>;
    }, []);
    

    const handleUpdateRefWithSubj = useCallback((subject, originalSubj) => {
        if (typeof updateRefWithSubj === 'function') {
            updateRefWithSubj(subject, originalSubj);
        }
    }, [updateRefWithSubj]);

    const handleCellDoubleClick = (header) => {
        // Prefer unsaved value if present
        const unsavedValue = unsavedChangesRef.current?.unsavedSubject?.[header.field];
        const rawValue = unsavedValue !== undefined ? unsavedValue : subject[header.field];
        logger.log(header)
    
        if (header.editable) {
            // if the value is imputed (decimal > 2 digits) then clear it out.
            const shouldClearValue = typeof rawValue === 'number' && rawValue % 1 !== 0 && rawValue.toString().split('.')[1].length > 2;
            setEditingCell(header.field);
            setEditableValue(shouldClearValue ? '' : rawValue); // Clear if decimal has more than 2 digits, otherwise keep value
        }
    };

    const handleSave = (value, force = null) => {
        // If the value is an empty string, revert to the original value
        if (value === '') {
            value = subject[editingCell]; // Revert to original value if blank
        }
    
        // Only update if the new value is different from the current value
        if (value !== subject[editingCell] || force === 'force') {
            // Get the existing unsaved changes
            const currentUnsaved = unsavedChangesRef.current.unsavedSubject || {};
    
            // Create a new object to hold the updated data
            const updatedSubject = { ...currentUnsaved, [editingCell]: value };
            logger.log(updatedSubject)
    
            // Ensure 'pid' is not duplicated, add it if it does not exist
            if (!updatedSubject.pid) {
                updatedSubject.pid = subject.PropertyInfo.parcel_id;
            }
    
            // Update the ref with the new merged data
            handleUpdateRefWithSubj(updatedSubject, subject);
        }
    
        // Reset the editing state
        setEditingCell(null);
    };

    const handleCancel = () => {
        setEditingCell(null);
    };

    const handlePropertyInfoClick = () => {
        // callback to pass up property info for subj record card
        handleSetPropertyInfo(subject,true);
    };

    const isMasterPetition = caseObject?.MasterPetition === 1;
    const isRepID = subject.RepID !== '';
    const rowTooltipTitle = isMasterPetition ? 'Master Petition Case' : isRepID ? `This is a case taken on from another representative (Representative ID # ${subject.RepID})` : '';
    const rowClassName = isMasterPetition
        ? 'items-center flex flex-shrink-0 bg-white border-b border-gray-300'
        : isRepID
            ? 'items-center flex flex-shrink-0 bg-yellow-200'
            : 'items-center flex flex-shrink-0 bg-white border-b border-gray-300';
    const rowStyle = isMasterPetition
        ? { borderTop: '1px solid #ddd', boxShadow: 'inset 0 0 0 2px rgba(255, 152, 0, 0.5)' }
        : { borderTop: '1px solid #ddd' };

    return (
        (isMasterPetition || isRepID) ? (
            <Tooltip title={rowTooltipTitle} arrow placement="top" disableInteractive>
                <div
                    className={rowClassName}
                    ref={containerRef}
                    style={rowStyle}
                >
                    {calculatedHeaders.map((header, index) => {
                        const rawValue = subject[header.field];
                        const valueType = header.type;
                        let value = rawValue;
                        if (rawValue !== undefined && rawValue !== null) {
                            value = header.valueGetter
                                ? header.valueGetter(subject[header.field])
                                : rawValue;
                        }

                        if (header.field === "time_adj") {
                            return (
                              <div
                                key={header.field}
                                className={`MuiDataGrid-cell hover:text-blue-500 ${header.align === "left" ? "MuiDataGrid-cell--textLeft" : ""} ${header.editable ? "MuiDataGrid-cell--editable" : ""}`}
                                role="gridcell"
                                tabIndex={header.editable ? "0" : "-1"}
                                style={{
                                  width: header.calcWidth,
                                  minWidth: header.calcWidth, maxWidth: header.calcWidth, flexShrink: 0,
                                  textAlign: "right",
                                }}
                              >
                                <TimeAdjustmentCell
                                  subject={subject}
                                  getTimeAdjustmentSettings={getTimeAdjustmentSettings()}
                                  IS_SALE_RUN={true}
                                />
                              </div>
                            );
                          }

                        const isUnsaved = unsavedChangesRef.current?.unsavedSubject?.[header.field] !== undefined;

                        // Determine if the header should use PropertyInfoTooltip
                        // const shouldRenderPropertyInfoTooltip = header.field === "PropertyInfo";

                        if(header.field==='actions' || header.field==='id'){ // custom rendering for action.
                            return(
                                <div style={{ width: header.calcWidth, minWidth: header.calcWidth, maxWidth: header.calcWidth, flexShrink: 0, outlineOffset: "-1px" }} key={header.field}>
                                <div className="flex items-center justify-center w-full h-full">
                                    <div
                                        className={`flex items-center justify-center w-5 h-5 rounded-full ${reviewed ? "bg-green-500" : ""}`}
                                        onClick={() => setReviewed(!reviewed)}
                                    >
                                        <CheckCircleOutlinedIcon className="hover:cursor-pointer" />
                                    </div>
                                </div>
                            </div>
                            )
                        }
                        if (header.field === 'PropertyInfo') {
                            return (
                                <div
                                    key={header.field}
                                    className="MuiDataGrid-cell hover:text-blue-500"
                                    role="gridcell"
                                    style={{
                                        width: header.calcWidth,
                                        minWidth: header.calcWidth, maxWidth: header.calcWidth, flexShrink: 0,
                                        cursor: 'pointer',
                                        color: isUnsaved ? '#facc15' : 'inherit',
                                        textDecoration: 'underline',
                                    }}
                                    onClick={handlePropertyInfoClick}
                                >
                                    <span>{subject.PropertyInfo?.Address || 'No Address'}</span>
                                </div>
                            );
                        }

                        // Special handling for photo columns
                        if (header.field === 'PhotoURL') {
                            return (
                                <div
                                    key={header.field}
                                    className="MuiDataGrid-cell photo-cell-no-padding hover:text-blue-500"
                                    role="gridcell"
                                    style={{
                                        width: header.calcWidth,
                                        minWidth: header.calcWidth, maxWidth: header.calcWidth, flexShrink: 0,
                                        padding: '0px !important',
                                        margin: '0px !important',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <PhotoCell
                                        parcelId={subject.parcel_id || subject.PID || subject.ParcelID || subject.id}
                                        photoUrl={subject.PhotoURL}
                                        MLSPhotoFolder={subject.MLSPhotoFolder}
                                        cellMode="edit"
                                        showUploadPrompt={false}
                                        style={{ width: '100%', height: '100%' }}
                                    />
                                </div>
                            );
                        }

                        return (
                            <div
                                key={header.field}
                                className={`MuiDataGrid-cell hover:text-blue-500 ${header.align === "left" ? "MuiDataGrid-cell--textLeft" : ""} ${header.editable ? "MuiDataGrid-cell--editable" : ""}`}
                                role="gridcell"
                                tabIndex={header.editable ? "0" : "-1"}
                                style={{
                                    width: header?.calcWidth,
                                    minWidth: header.calcWidth, maxWidth: header.calcWidth, flexShrink: 0,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    // borderLeft: "2px solid #ddd",
                                    outlineOffset: "-1px",
                                    textOverflow: "ellipsis",
                                    textAlign: header.type === "number" ? "right" : header.align,
                                    color: isUnsaved ? '#facc15' : 'inherit', // Apply color if unsaved
                                }}
                                onDoubleClick={() => handleCellDoubleClick(header)}
                                >
                                    {/* There is an issue w the imputed value check here */}
                                {editingCell === header.field ? (
                                    <EditingCell
                                        value={editableValue}
                                        type={header.type}
                                        width={header.calcWidth}
                                        fieldName={header.field}
                                        valueOptions={header.valueOptions}
                                        onSave={handleSave}
                                        onCancel={handleCancel}
                                    />
                                ) : isUnsaved ? (
                                    unsavedChangesRef.current.unsavedSubject[header.field]
                                ) : (
                                                                    // This ordering seems wrong - it should checkf or rendercell first but this errors if you swap atm
                                header.valueGetter
                                    ? imputedValueCheck(header, value)
                                    : header.renderCell
                                    ? header.renderCell({ value: value, row: subject })
                                    : value
                                )}
                            </div>
                        );
                    })}
                    <div style={{ minWidth: "15px", maxWidth: "15px", outlineOffset: "-1px" }}></div>
                </div>
            </Tooltip>
        ) : (
            <div
                className={rowClassName}
                ref={containerRef}
                style={rowStyle}
            >
                {calculatedHeaders.map((header, index) => {
                    const rawValue = subject[header.field];
                    const valueType = header.type;
                    let value = rawValue;
                    if (rawValue !== undefined && rawValue !== null) {
                        value = header.valueGetter
                            ? header.valueGetter(subject[header.field])
                            : rawValue;
                    }

                    if (header.field === "time_adj") {
                        return (
                          <div
                            key={header.field}
                            className={`MuiDataGrid-cell hover:text-blue-500 ${header.align === "left" ? "MuiDataGrid-cell--textLeft" : ""} ${header.editable ? "MuiDataGrid-cell--editable" : ""}`}
                            role="gridcell"
                            tabIndex={header.editable ? "0" : "-1"}
                            style={{
                              width: header.calcWidth,
                              minWidth: header.calcWidth, maxWidth: header.calcWidth, flexShrink: 0,
                              textAlign: "right",
                            }}
                          >
                            <TimeAdjustmentCell
                              subject={subject}
                              getTimeAdjustmentSettings={getTimeAdjustmentSettings()}
                              IS_SALE_RUN={true}
                            />
                          </div>
                        );
                      }

                    const isUnsaved = unsavedChangesRef.current?.unsavedSubject?.[header.field] !== undefined;

                    // Determine if the header should use PropertyInfoTooltip
                    // const shouldRenderPropertyInfoTooltip = header.field === "PropertyInfo";

                    if(header.field==='actions' || header.field==='id'){ // custom rendering for action.
                        return(
                            <div style={{ width: header.calcWidth, minWidth: header.calcWidth, maxWidth: header.calcWidth, flexShrink: 0, outlineOffset: "-1px" }} key={header.field}>
                            <div className="flex items-center justify-center w-full h-full">
                                <div
                                    className={`flex items-center justify-center w-5 h-5 rounded-full ${reviewed ? "bg-green-500" : ""}`}
                                    onClick={() => setReviewed(!reviewed)}
                                >
                                    <CheckCircleOutlinedIcon className="hover:cursor-pointer" />
                                </div>
                            </div>
                        </div>
                        )
                    }
                    if (header.field === 'PropertyInfo') {
                        return (
                            <div
                                key={header.field}
                                className="MuiDataGrid-cell hover:text-blue-500"
                                role="gridcell"
                                style={{
                                    width: header.calcWidth,
                                    minWidth: header.calcWidth, maxWidth: header.calcWidth, flexShrink: 0,
                                    cursor: 'pointer',
                                    color: isUnsaved ? '#facc15' : 'inherit',
                                    textDecoration: 'underline',
                                }}
                                onClick={handlePropertyInfoClick}
                            >
                                <span>{subject.PropertyInfo?.Address || 'No Address'}</span>
                            </div>
                        );
                    }

                    // Special handling for photo columns
                    if (header.field === 'PhotoURL') {
                        return (
                            <div
                                key={header.field}
                                className="MuiDataGrid-cell photo-cell-no-padding hover:text-blue-500"
                                role="gridcell"
                                style={{
                                    width: header.calcWidth,
                                    minWidth: header.calcWidth, maxWidth: header.calcWidth, flexShrink: 0,
                                    padding: '0px !important',
                                    margin: '0px !important',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <PhotoCell
                                    parcelId={subject.parcel_id || subject.PID || subject.ParcelID || subject.id}
                                    photoUrl={subject.PhotoURL}
                                    MLSPhotoFolder={subject.MLSPhotoFolder}
                                    cellMode="edit"
                                    showUploadPrompt={false}
                                    style={{ width: '100%', height: '100%' }}
                                />
                            </div>
                        );
                    }

                    return (
                        <div
                            key={header.field}
                            className={`MuiDataGrid-cell hover:text-blue-500 ${header.align === "left" ? "MuiDataGrid-cell--textLeft" : ""} ${header.editable ? "MuiDataGrid-cell--editable" : ""}`}
                            role="gridcell"
                            tabIndex={header.editable ? "0" : "-1"}
                            style={{
                                width: header?.calcWidth,
                                minWidth: header.calcWidth, maxWidth: header.calcWidth, flexShrink: 0,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                // borderLeft: "2px solid #ddd",
                                outlineOffset: "-1px",
                                textOverflow: "ellipsis",
                                textAlign: header.type === "number" ? "right" : header.align,
                                color: isUnsaved ? '#facc15' : 'inherit', // Apply color if unsaved
                            }}
                            onDoubleClick={() => handleCellDoubleClick(header)}
                            >
                                {/* There is an issue w the imputed value check here */}
                            {editingCell === header.field ? (
                                <EditingCell
                                    value={editableValue}
                                    type={header.type}
                                    width={header.calcWidth}
                                    fieldName={header.field}
                                    valueOptions={header.valueOptions}
                                    onSave={handleSave}
                                    onCancel={handleCancel}
                                />
                            ) : isUnsaved ? (
                                unsavedChangesRef.current.unsavedSubject[header.field]
                            ) : (
                                // This ordering seems wrong - it should checkf or rendercell first but this errors if you swap atm
                                header.valueGetter
                                    ? imputedValueCheck(header, value)
                                    : header.renderCell
                                    ? header.renderCell({ value: value, row: subject })
                                    : value
                            )}
                        </div>
                    );
                })}
                <div style={{ minWidth: "15px", maxWidth: "15px", outlineOffset: "-1px" }}></div>
            </div>
        )
    );
};

if (process.env.NODE_ENV === 'development') {
  SubjectDataGridRow.whyDidYouRender = true;
}

export default SubjectDataGridRow;

const EditingCell = React.memo(({ value, onSave, onCancel, type, width, fieldName, valueOptions }) => {
    logger.log('render edit')
    logger.log(type)
    const [inputValue, setInputValue] = useState(value);
    const inputRef = useRef(null);
    
        useEffect(() => {
            const handleClickOutside = (event) => {
                 // special handling for onclick function was required here (the input change wasn't working b/c click out upate fires first)
                if (event.target.tagName === 'LI') {
                    onSave(event.target.innerText,'force'); // add force param to force the update to go through / delineate between a regular save event.
                }
                if (inputRef.current && !inputRef.current.contains(event.target)) {
                    onSave(inputValue);
                }
            };
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }, [inputValue, onSave]);
    
        const handleChange = (event) => {
            setInputValue(event.target.value);
        };

        const handleKeyDown = (event) => {
            if (event.key === 'Enter') {
                onSave(inputValue);
            }
            if (event.key === 'Escape') {
                onCancel();
            }
        };
    
        return (
            <div
                ref={inputRef}
                style={{ 
                    width: width || '100%', // Ensure full width if not specified
                    height: '100%', // Full height of parent
                    display: 'flex', // Enable flex for children
                    alignItems: 'center', // Center items vertically
                    padding: 0, // Remove default padding
                    boxSizing: 'border-box', // Include border in element's total width and height
                    position: 'relative', // Ensure the container is positioned for children to expand
                }}
            >
                {type === 'singleSelect' && (
                    <Select
                        value={inputValue}
                        autoFocus
                        fullWidth
                        defaultOpen={true}
                        MenuProps={{ // this needs to be updated to open in the same manner as the grid one. but works fo rnow.
                            anchorOrigin: {
                              vertical: 'top',
                              horizontal: 'center'
                            },
                            transformOrigin: {
                              vertical: 'bottom',
                              horizontal: 'center'
                            }
                          }}
                        sx={{
                            flex: 1, // Ensure TextField expands to full width
                            height: '100%', // Ensure full height of parent container
                            padding: 0, // Remove internal padding
                            margin: 0, // Remove margins
                            '& .MuiOutlinedInput-root': {
                                border: '2px solid #ddd',
                                borderRadius: 0,
                                height: '100%', // Ensure full height of the input
                            },
                            '& .MuiInputBase-input': {
                                paddingTop: 0,
                                paddingBottom: 0,
                                height: '100%', // Ensure full height of the input
                                boxSizing: 'border-box', // Include padding and border in height calculation
                            },
                            // Optional: Override specific styles if needed
                            '& .css-1t8l2tu-MuiInputBase-input-MuiOutlinedInput-input': {
                                paddingTop: 0,
                                paddingBottom: 0,
                            },
                        }}
                    >
                        {(valueOptions || []).map((option) => (
                            <MenuItem key={option} value={option}
                            >
                                {option}
                            </MenuItem>
                        ))}
                    </Select>
                )}
                {type === 'number' && (
                    <TextField
                        type="number"
                        value={inputValue}
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                        autoFocus
                        fullWidth
                        sx={{
                            flex: 1, // Ensure TextField expands to full width
                            height: '100%', // Ensure full height of parent container
                            padding: 0, // Remove internal padding
                            margin: 0, // Remove margins
                            '& .MuiOutlinedInput-root': {
                                border: '2px solid #ddd',
                                borderRadius: 0,
                                height: '100%', // Ensure full height of the input
                            },
                            '& .MuiInputBase-input': {
                                paddingTop: 0,
                                paddingBottom: 0,
                                height: '100%', // Ensure full height of the input
                                boxSizing: 'border-box', // Include padding and border in height calculation
                            },
                            // Optional: Override specific styles if needed
                            '& .css-1t8l2tu-MuiInputBase-input-MuiOutlinedInput-input': {
                                paddingTop: 0,
                                paddingBottom: 0,
                            },
                        }}
                    />
                )}
                {type === 'text' && (
                    <TextField
                        type="text"
                        value={inputValue}
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                        autoFocus
                        fullWidth
                        InputProps={{
                            sx: { 
                                padding: 0, // Remove padding
                                height: '100%', // Ensure full height
                                boxSizing: 'border-box', // Include padding and border in height calculation
                            }
                        }}
                    />
                )}
            </div>
        );
    }
    );
    